import { useLocale } from "@acto/web-common/locales";
import { useCurrencyContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { currencyPattern } from "@acto/taskpane/core/utils/currencyPattern";

function shortenNumber(number: number, maximumFractionDigits: number, locale: string): string {
  let value: number;
  let suffix = "";
  const absNumber = Math.abs(number);

  if (absNumber >= 1e9) {
    value = number / 1e9;
    suffix = "B";
  } else if (absNumber >= 1e6) {
    value = number / 1e6;
    suffix = "M";
  } else if (absNumber >= 1e3) {
    value = number / 1e3;
    suffix = "K";
  } else {
    value = number;
  }

  const formattedValue = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits,
  }).format(value);

  return `${formattedValue}${suffix}`;
}

interface NumberFormatterProps {
  number: number;
  type: "currency" | "percentage" | "number";
  shorten?: boolean;
  addPlusSign?: boolean;
  maximumFractionDigits?: number;
}

export const useNumberFormatter = () => {
  const { locale } = useLocale();
  const currency = useCurrencyContext();

  const formatNumber = ({
    number,
    type,
    shorten = false,
    addPlusSign = false,
    maximumFractionDigits = 0,
  }: NumberFormatterProps) => {
    let formattedNumber: string;
    const commonOptions = {
      minimumFractionDigits: 0,
    };

    if (shorten) {
      formattedNumber = shortenNumber(number, maximumFractionDigits, locale);
    } else {
      const numberFormatter = new Intl.NumberFormat(locale, {
        ...commonOptions,
        style: "decimal",
        maximumFractionDigits,
      });
      formattedNumber = numberFormatter.format(number);
    }

    switch (type) {
      case "currency": {
        const { symbol, symbolFirst, spaced } = currencyPattern(locale, currency);
        const sign = number < 0 ? "-" : addPlusSign ? "+" : "";
        const magnitude = formattedNumber.replace("-", "");
        const gap = spaced ? "\u00a0" : "";
        formattedNumber = symbolFirst ? `${sign}${symbol}${gap}${magnitude}` : `${sign}${magnitude}${gap}${symbol}`;
        break;
      }
      case "number":
        formattedNumber = `${addPlusSign && number > 0 ? "+" : ""}${formattedNumber}`;
        break;
      case "percentage":
        formattedNumber = `${addPlusSign && number > 0 ? "+" : ""}${formattedNumber}%`;
        break;
      default:
        throw new Error("Invalid type for number formatting");
    }
    return formattedNumber;
  };

  return formatNumber;
};
